import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Download,
  Upload,
  Eye,
  FileText,
  Building,
  DollarSign,
  Calendar,
  Users,
  ArrowUpDown,
  CheckCircle,
  Clock,
  Play,
  FileCheck,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format } from 'date-fns';
import { PaymentTaskDetailModal } from './PaymentTaskDetailModal';
import type { PaymentStage } from '../TumPaymentReport';

interface PaymentTaskListProps {
  stage: PaymentStage;
  customerFilter: string[];
  propertyFilter: string[];
  accountFilter: string[];
  commodityFilter: string[];
  paymentTaskIdFilter: string;
}

interface PaymentTask {
  id: string;
  payment_task_id: string;
  status: PaymentStage;
  customer_name: string;
  vendor_name: string;
  bills_count: number;
  total_amount: number;
  last_updated: string;
  created_at: string;
  created_by?: string;
}

export function PaymentTaskList({
  stage,
  customerFilter,
  propertyFilter,
  accountFilter,
  commodityFilter,
  paymentTaskIdFilter,
}: PaymentTaskListProps) {
  const { currentWorkspace } = useWorkspace();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Generate dummy payment tasks for demo
  const dummyTasks: PaymentTask[] = useMemo(() => {
    const tasks: PaymentTask[] = [];
    const customers = ['Customer A', 'Customer B', 'Customer C', 'Customer D'];
    const vendors = ['Duke Energy', 'PG&E', 'Southern California Edison', 'Con Edison'];
    const statuses: PaymentStage[] = ['payment-ready', 'funds-requested', 'funds-received', 'payment-initiated', 'paid'];
    
    // Generate tasks based on stage
    const stageIndex = statuses.indexOf(stage);
    const baseDate = new Date('2025-12-15');
    
    for (let i = 0; i < 8; i++) {
      const taskDate = new Date(baseDate);
      taskDate.setDate(taskDate.getDate() - i);
      
      tasks.push({
        id: `task-${stage}-${i + 1}`,
        payment_task_id: `PT-${stage.toUpperCase().slice(0, 2)}-${String(i + 1).padStart(4, '0')}`,
        status: stage,
        customer_name: customers[i % customers.length],
        vendor_name: vendors[i % vendors.length],
        bills_count: Math.floor(Math.random() * 20) + 5,
        total_amount: Math.random() * 50000 + 1000,
        last_updated: taskDate.toISOString(),
        created_at: taskDate.toISOString(),
        created_by: 'System User',
      });
    }
    
    return tasks;
  }, [stage]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let filtered = [...dummyTasks];
    
    if (paymentTaskIdFilter && paymentTaskIdFilter.trim() !== '') {
      filtered = filtered.filter(task => 
        task.payment_task_id.toLowerCase().includes(paymentTaskIdFilter.toLowerCase())
      );
    }
    
    // Note: In a real implementation, customer, property, account, and commodity filters
    // would be applied at the query level. For demo, we're showing all tasks.
    
    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortField as keyof PaymentTask];
      const bValue = b[sortField as keyof PaymentTask];
      
      if (sortField === 'created_at' || sortField === 'last_updated') {
        return sortDirection === 'asc'
          ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
          : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return 0;
    });
    
    return filtered;
  }, [dummyTasks, paymentTaskIdFilter, sortField, sortDirection]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MM/dd/yyyy HH:mm');
  };

  const getStageBadge = (status: PaymentStage) => {
    const variants = {
      'payment-ready': { variant: 'secondary' as const, label: 'Payment Ready', icon: FileText, color: 'text-blue-600' },
      'funds-requested': { variant: 'default' as const, label: 'Funds Requested', icon: Clock, color: 'text-amber-600' },
      'funds-received': { variant: 'default' as const, label: 'Funds Received', icon: CheckCircle, color: 'text-green-600' },
      'payment-initiated': { variant: 'default' as const, label: 'Payment Initiated', icon: Play, color: 'text-purple-600' },
      'paid': { variant: 'default' as const, label: 'Paid', icon: FileCheck, color: 'text-emerald-600' },
    };
    
    const config = variants[status];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1.5">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const getExportLabel = () => {
    switch (stage) {
      case 'payment-ready':
        return 'Export Funds Requested';
      case 'funds-requested':
        return 'Export Funds Requested';
      case 'funds-received':
        return 'Export Vendor Payment';
      case 'payment-initiated':
        return 'Export Vendor Payment';
      case 'paid':
        return 'Export Reconciliation';
      default:
        return 'Export';
    }
  };

  const canImport = stage === 'funds-received' || stage === 'paid';

  const handleSelectAll = () => {
    if (selectedTasks.size === filteredTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasks.map(task => task.id)));
    }
  };

  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleBulkAction = (action: string) => {
    // Handle bulk actions like export, import, delete, etc.
    console.log(`Bulk action: ${action} on ${selectedTasks.size} tasks`);
  };

  const SortableHeader = ({ field, children, icon }: { field: string; children: React.ReactNode; icon?: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors" 
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2 font-semibold">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Payment Tasks</p>
                <p className="text-3xl font-bold">{filteredTasks.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Bills</p>
                <p className="text-2xl font-bold">
                  {filteredTasks.reduce((sum, task) => sum + task.bills_count, 0)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(filteredTasks.reduce((sum, task) => sum + task.total_amount, 0))}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Tasks Table */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Payment Tasks - {stage.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</CardTitle>
                <CardDescription>Manage payment tasks at this stage</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedTasks.size > 0 && (
                <>
                  <Badge variant="secondary" className="gap-1">
                    {selectedTasks.size} selected
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <MoreHorizontal className="h-4 w-4" />
                        Bulk Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleBulkAction('export')}>
                        <Download className="h-4 w-4 mr-2" />
                        Export Selected
                      </DropdownMenuItem>
                      {canImport && (
                        <DropdownMenuItem onClick={() => handleBulkAction('import')}>
                          <Upload className="h-4 w-4 mr-2" />
                          Import CSV for Selected
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleBulkAction('create-task')}>
                        <FileText className="h-4 w-4 mr-2" />
                        Create Payment Task
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleBulkAction('delete')}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {canImport && (
                <Button variant="outline" size="sm" className="gap-2">
                  <Upload className="h-4 w-4" />
                  Import CSV
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                {getExportLabel()}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedTasks.size === filteredTasks.length && filteredTasks.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <SortableHeader field="payment_task_id" icon={<FileText className="h-4 w-4" />}>
                    Payment Task ID
                  </SortableHeader>
                  <SortableHeader field="customer_name" icon={<Users className="h-4 w-4" />}>
                    Customer
                  </SortableHeader>
                  <SortableHeader field="vendor_name" icon={<Building className="h-4 w-4" />}>
                    Vendor
                  </SortableHeader>
                  <SortableHeader field="bills_count" icon={<FileText className="h-4 w-4" />}>
                    Bills Count
                  </SortableHeader>
                  <SortableHeader field="total_amount" icon={<DollarSign className="h-4 w-4" />}>
                    Total Amount
                  </SortableHeader>
                  <TableHead>
                    <div className="flex items-center gap-2 font-semibold">
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />
                      Status
                    </div>
                  </TableHead>
                  <SortableHeader field="last_updated" icon={<Calendar className="h-4 w-4" />}>
                    Last Updated
                  </SortableHeader>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="h-12 w-12 text-muted-foreground/50" />
                        <p className="text-muted-foreground font-medium">No payment tasks found</p>
                        <p className="text-sm text-muted-foreground">Create a payment task to get started</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map((task) => (
                    <TableRow
                      key={task.id}
                      className={`hover:bg-muted/50 transition-colors ${
                        selectedTasks.has(task.id) ? 'bg-primary/5' : ''
                      }`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedTasks.has(task.id)}
                          onCheckedChange={() => handleSelectTask(task.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${task.payment_task_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono font-medium">{task.payment_task_id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{task.customer_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{task.vendor_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => setSelectedTaskId(task.id)}
                          className="flex items-center gap-2 text-primary hover:underline font-medium"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {task.bills_count}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-semibold">{formatCurrency(task.total_amount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStageBadge(task.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{formatDate(task.last_updated)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {task.status === 'payment-initiated' || task.status === 'paid' ? (
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={task.status === 'paid' ? 'default' : 'secondary'} 
                              className="gap-1.5"
                            >
                              <RefreshCw className="h-3 w-3" />
                              {task.status === 'paid' ? 'Reconciled' : 'Ready to Reconcile'}
                            </Badge>
                            {task.status === 'payment-initiated' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Open reconciliation import
                                }}
                              >
                                <Upload className="h-3 w-3" />
                                Import
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setSelectedTaskId(task.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Task Detail Modal */}
      {selectedTaskId && (
        <PaymentTaskDetailModal
          taskId={selectedTaskId}
          stage={stage}
          open={!!selectedTaskId}
          onOpenChange={(open) => !open && setSelectedTaskId(null)}
        />
      )}
    </div>
  );
}

